import { AUDIO_MUTE, DESKTOP_MUTE, VIDEO_MUTE, createRemoteMuteConfirmedEvent, createToolbarEvent } from '../analytics/AnalyticsEvents';
import { sendAnalytics } from '../analytics/functions';
import { setAudioMuted, setScreenshareMuted, setVideoMuted } from '../base/media/actions';
import { MEDIA_TYPE, SCREENSHARE_MUTISM_AUTHORITY, VIDEO_MUTISM_AUTHORITY } from '../base/media/constants';
import { muteRemoteParticipant } from '../base/participants/actions';
import { getRemoteParticipants } from '../base/participants/functions';
import logger from './logger';
/**
 * Mutes the local participant.
 *
 * @param {boolean} enable - Whether to mute or unmute.
 * @param {MEDIA_TYPE} mediaType - The type of the media channel to mute.
 * @returns {Function}
 */
export function muteLocal(enable, mediaType) {
    return (dispatch) => {
        switch (mediaType) {
            case MEDIA_TYPE.AUDIO: {
                sendAnalytics(createToolbarEvent(AUDIO_MUTE, { enable }));
                dispatch(setAudioMuted(enable, /* ensureTrack */ true));
                break;
            }
            case MEDIA_TYPE.SCREENSHARE: {
                sendAnalytics(createToolbarEvent(DESKTOP_MUTE, { enable }));
                dispatch(setScreenshareMuted(enable, SCREENSHARE_MUTISM_AUTHORITY.USER, /* ensureTrack */ true));
                break;
            }
            case MEDIA_TYPE.VIDEO: {
                sendAnalytics(createToolbarEvent(VIDEO_MUTE, { enable }));
                dispatch(setVideoMuted(enable, VIDEO_MUTISM_AUTHORITY.USER, /* ensureTrack */ true));
                break;
            }
            default: {
                logger.error(`Unsupported media type: ${mediaType}`);
                return;
            }
        }
    };
}
/**
 * Mutes the remote participant with the given ID.
 *
 * @param {string} participantId - ID of the participant to mute.
 * @param {MEDIA_TYPE} mediaType - The type of the media channel to mute.
 * @returns {Function}
 */
export function muteRemote(participantId, mediaType) {
    return (dispatch) => {
        sendAnalytics(createRemoteMuteConfirmedEvent(participantId, mediaType));
        // TODO(saghul): reconcile these 2 types.
        const muteMediaType = mediaType === MEDIA_TYPE.SCREENSHARE ? 'desktop' : mediaType;
        dispatch(muteRemoteParticipant(participantId, muteMediaType));
        // Belltroop: Per-participant approval revocation now happens on the
        // PARTICIPANT side, in av-moderation/middleware.ts, in response to
        // the standard *_MUTED_BY_FOCUS events that already fire from the
        // mute IQ above. We previously dispatched rejectParticipant* here,
        // but that took an extra XMPP round-trip via Prosody's whitelist
        // and caused the moderator's lib-jitsi-meet whitelist to drift out
        // of sync after the second mute, breaking the second unmute.
    };
}
/**
 * Mutes all participants.
 *
 * @param {Array<string>} exclude - Array of participant IDs to not mute.
 * @param {MEDIA_TYPE} mediaType - The media type to mute.
 * @returns {Function}
 */
export function muteAllParticipants(exclude, mediaType) {
    return (dispatch, getState) => {
        const state = getState();
        getRemoteParticipants(state).forEach((p, id) => {
            if (exclude.includes(id)) {
                return;
            }
            // Belltroop: rejectParticipant* dispatches removed — see
            // muteRemote() above. Each participant now resets their own
            // approval via the *_MUTED_BY_FOCUS listener in
            // av-moderation/middleware.ts.
            dispatch(muteRemote(id, mediaType));
        });
    };
}
